import EmptyState from "~/components/build/Show/components/Sidebar/EmptyState";
import { TreeItem } from "~/components/build/Show/components/Sidebar/TreeItem/TreeItem";
import { Range, defaultRangeExtractor, useVirtualizer } from "@tanstack/react-virtual";
import { FlatTreeNode } from "~/components/build/Show/components/Sidebar/useTreeNodes";
import { useScrollToId } from "~/components/build/Show/components/Sidebar/useScrollToId";
import { TreeNodeType } from "~/components/build/Show/components/Sidebar/TreeNode";
import { DisplayMode } from "~/components/build/Show/components/Sidebar/useFilterStore";
import { useCallback, useRef } from "react";
import { CollapsibleState } from "~/components/build/Show/components/Sidebar/TreeItem/CollapsibleState";
import { useBuildStore } from "~/components/build/Show/lib/useBuildStore";

export const LIST_ITEM_HEIGHT = 28;
export const SIDEBAR_HEADER_HEIGHT = 42;
export const SIDEBAR_HEADER_EXPANDED_HEIGHT = 80;
const MAX_STICKY_HEADER_DEPTH = LIST_ITEM_HEIGHT * 2;

interface StepTreeProps {
  /**
   * Ref for scrollable element with overflow-scroll
   */
  scrollRef: React.RefObject<HTMLDivElement>;

  /**
   * Tree display mode
   */
  displayMode: DisplayMode;

  /**
   * Flat list of nodes to render.
   */
  nodes: FlatTreeNode[];
}

function isExpanded(node: FlatTreeNode) {
  return (
    node.collapsibleState === CollapsibleState.Expanded ||
    node.collapsibleState === CollapsibleState.Full
  );
}

/**
 * Render a virtualised tree of states, steps, and jobs with sticky headers
 */
export function StepTree({ nodes, displayMode, scrollRef }: StepTreeProps) {
  const isSearchOpen = useBuildStore((state) => state.isSearchOpen);

  // Calculate sticky offset
  const getStickyTop = useCallback(
    (node) => {
      const headerHeight = isSearchOpen ? SIDEBAR_HEADER_EXPANDED_HEIGHT : SIDEBAR_HEADER_HEIGHT;
      return headerHeight + node.depth * LIST_ITEM_HEIGHT;
    },
    [isSearchOpen],
  );

  const activeStickyIndexRef = useRef<number[]>([]);
  const isActiveSticky = (index: number) => activeStickyIndexRef.current.includes(index);

  // Custom range extractor that includes sticky items
  const rangeExtractor = useCallback(
    (range: Range) => {
      // The top node in the virtualizer. This may or may not be hidden behind sticky content.
      const currentNode = nodes[range.startIndex];

      // An array of active sticky items.
      activeStickyIndexRef.current = [];

      // Calculate the current start index offset (ie. depth) for the current item that isn't hidden.
      const startIndexOffset =
        currentNode.parentPath.length > 0
          ? currentNode.parentPath.length
          : isExpanded(currentNode)
            ? 1
            : 0;

      // Based on the index offset, find the current node that we should be rendering sticky items for.
      const scrollOffset = (rowVirtualizer.scrollOffset ?? 0) + startIndexOffset * LIST_ITEM_HEIGHT;
      const currentOffsetIndex = rowVirtualizer.getVirtualItemForOffset(scrollOffset)?.index ?? 0;
      const currentOffsetNode = nodes[currentOffsetIndex];

      if (currentOffsetNode.depth >= currentNode.depth) {
        // If the node is expanded, make it sticky.
        if (isExpanded(currentOffsetNode)) {
          activeStickyIndexRef.current.push(currentOffsetNode.index);
        }

        // If the node has parents, make them sticky.
        activeStickyIndexRef.current.push(
          ...currentOffsetNode.parentPath.map((path) => path.index),
        );
      } else {
        // Keep prev sticky items around as we move between different depths
        activeStickyIndexRef.current.push(...currentNode.parentPath.map((path) => path.index));
      }

      // Get the default range of visible items
      const defaultRange = defaultRangeExtractor(range);

      // Combine default range with sticky indices and sort
      const allIndices = new Set([...defaultRange, ...activeStickyIndexRef.current]);
      return Array.from(allIndices).sort((indexA, indexB) => indexA - indexB);
    },
    [nodes],
  );

  /**
   * Configure a virtualizer for the nodes.
   */
  const rowVirtualizer = useVirtualizer({
    count: nodes.length,
    getItemKey: (index) => nodes[index].id,
    getScrollElement: useCallback(() => scrollRef.current, [scrollRef]),
    estimateSize: (index) => {
      const node = nodes[index];
      if (node.type === TreeNodeType.Spacer) {
        return LIST_ITEM_HEIGHT / 3;
      }
      return LIST_ITEM_HEIGHT;
    },
    overscan: 50,
    rangeExtractor,
    scrollPaddingStart: MAX_STICKY_HEADER_DEPTH,
    scrollPaddingEnd: MAX_STICKY_HEADER_DEPTH,
  });

  // Handle auto-scrolling to a node when the URL changes.
  useScrollToId(rowVirtualizer, nodes);

  if (nodes.length === 0) {
    return <EmptyState />;
  }

  return (
    <div className="p-1 md:p-0">
      <div role="tree" style={{ height: rowVirtualizer.getTotalSize() }} className="relative">
        {rowVirtualizer.getVirtualItems().map((virtualItem) => {
          const node = nodes[virtualItem.index];
          const isSticky = isActiveSticky(virtualItem.index);

          return (
            <TreeItem
              {...node}
              totalCount={nodes.length} // todo: this should be the count of the group/level
              depth={displayMode === DisplayMode.State ? node.depth - 1 : node.depth}
              key={virtualItem.key}
              style={{
                position: isSticky ? "sticky" : "absolute",
                top: isSticky ? getStickyTop(node) : 0,
                transform: isSticky ? undefined : `translateY(${virtualItem.start}px)`,
                height: `${virtualItem.size}px`,
                left: 0,
                width: "100%",
                zIndex: 20 - node.depth,
              }}
            >
              {node.render({
                ...node,
                depth: displayMode === DisplayMode.State ? node.depth - 1 : node.depth,
              })}
            </TreeItem>
          );
        })}
      </div>
    </div>
  );
}
